import React from 'react';

import {
  Select,
  styled,
  Grid,
  InputAction,
  Typography,
  CloseIcon,
  TopIcon,
  Tooltip,
  SwapHorizIcon,
  IconButton,
} from '@gogame/components';
import { useScreenDispatch } from '@gogame/screens';

import {
  OPEN_MULTIPLE_ROWS,
  CHANGE_BREAKDOWN_PATH,
  REMOVE_BREAKDOWN_PATH,
  SWAP_DIMENSIONS,
} from 'scenes/Trackers/Tracker/reducers';
import {
  CLEAR_DIMENSION,
  SET_DIMENSION,
} from 'scenes/Trackers/Tracker/reducers';
import { useGetScreenStateProp } from 'scenes/Trackers/Tracker/useGetScreenStateProp';

import { useGetTopRows } from './useGetTopRows';

const FixedGrid = styled(Grid)`
  width: 160px;
`;

const DimensionSelect = (props) => <Select {...props} dropdownWidth={256} />;

const BREAKDOWN_LIMIT = 5;
const TOP_ROWS_MAX_LEVEL = 1;

window.dataLayer = window.dataLayer || [];

const Swap = ({ onClick }) => {
  return (
    <Grid item style={{ margin: `0 -20px` }}>
      <IconButton
        size={'small'}
        fontSize={15}
        icon={<SwapHorizIcon />}
        tooltip={'Swap dimensions'}
        onClick={onClick}
      />
    </Grid>
  );
};

export const PathBuilder = ({
  dimensions,
  drillTo,
  nextLevel,

  onChangeNextLevel,
  rows,
}) => {
  const drillPath = useGetScreenStateProp('drillPath');
  const dispatch = useScreenDispatch();
  const breakdownPath = useGetScreenStateProp('breakdownPath');
  const dimension = [...drillPath].pop()?.type;
  const getTopRows = useGetTopRows({ rows });

  const handleChange = async (_e, value) => {
    dispatch({
      type: SET_DIMENSION,
      payload: value,
    });
  };

  const handleClearDimension = async () => {
    dispatch({
      type: CLEAR_DIMENSION,
    });
  };

  // const handleAddBreakdown = ([{ id }]) => {
  //   dispatch({
  //     type: ADD_BREAKDOWN_PATH,
  //     payload: { breakdownId: id },
  //   });
  // };

  const getSwapHandler = (left, right) => () => {
    dispatch({
      type: SWAP_DIMENSIONS,
      payload: { left, right },
    });
  };

  const getChangeHandler = (index) => (_e, breakdownId) => {
    dispatch({
      type: CHANGE_BREAKDOWN_PATH,
      payload: { index, breakdownId },
    });
  };

  const getRemoveHandler = (index) => () => {
    dispatch({
      type: REMOVE_BREAKDOWN_PATH,
      payload: { index },
    });
  };

  const isBreakdownDisabled = ({ id }) =>
    breakdownPath.some(({ breakdownId }) => id === breakdownId);

  const breakdownPathLength = breakdownPath.length;

  const handleExpandTop = (openRows, level) => () => {
    const topRows = getTopRows(openRows);
    window.dataLayer.push({
      event: 'openTop5',
      breakdownLevel: level + 1,
    });

    dispatch({
      type: OPEN_MULTIPLE_ROWS,
      payload: {
        ids: topRows.map(({ id }) => id),
        level: level,
      },
    });
  };

  const top = (
    <Tooltip title={'Expand first 5 rows'}>
      <TopIcon />
    </Tooltip>
  );

  return (
    <Grid spacing={2.5} container alignItems={'center'}>
      <FixedGrid item>
        <DimensionSelect
          options={dimensions}
          onChange={handleChange}
          value={dimension}
          label={'Dimension'}
          actions={
            breakdownPath?.length && [
              <InputAction icon={top} onClick={handleExpandTop([], 0)} />,
              <InputAction
                icon={<CloseIcon />}
                onClick={handleClearDimension}
              />,
            ]
          }
        />
      </FixedGrid>
      {breakdownPath?.length > 0 && (
        <Swap
          onClick={getSwapHandler(
            { level: -1, value: dimension },
            { level: 0, value: breakdownPath[0]?.breakdownId }
          )}
        />
      )}

      {breakdownPath?.map((breakdown, i) => {
        const { breakdownId } = breakdown;
        const level = i + 1;
        const { openRows = [] } = breakdownPath[i] || {};
        const nextStep = breakdownPath[i + 1];

        return (
          <React.Fragment key={`${breakdownId}_${i}`}>
            <FixedGrid key={`${breakdownId}_${i}`} item>
              <DimensionSelect
                options={drillTo}
                onChange={getChangeHandler(i)}
                isOptionDisabled={isBreakdownDisabled}
                value={breakdownId}
                label={getLevelLabel(i)}
                actions={[
                  ...(openRows.length && // has open rows
                  level <= TOP_ROWS_MAX_LEVEL && // less or equal max level
                  nextStep // has next step
                    ? [
                        <InputAction
                          icon={top}
                          onClick={handleExpandTop(openRows, level)}
                        />,
                      ]
                    : []),
                  <InputAction
                    icon={<CloseIcon />}
                    onClick={getRemoveHandler(i)}
                  />,
                ]}
              />
            </FixedGrid>

            {nextStep && (
              <Swap
                onClick={getSwapHandler(
                  { level: i, value: breakdownId },
                  { level: i + 1, value: breakdownPath[i + 1]?.breakdownId }
                )}
              />
            )}
          </React.Fragment>
        );
      })}
      {breakdownPathLength < BREAKDOWN_LIMIT && (
        <FixedGrid item>
          <DimensionSelect
            options={drillTo}
            onChange={getChangeHandler(breakdownPathLength)}
            isOptionDisabled={isBreakdownDisabled}
            value={breakdownPath[breakdownPathLength]}
            label={getLevelLabel(breakdownPathLength)}
          />
        </FixedGrid>
      )}
      <Grid item sx={{ margin: `0 -20px` }}>
        <Typography
          sx={{ alignSelf: 'center', textAlign: 'center', width: '20px' }}
        >
          /
        </Typography>
      </Grid>
      <FixedGrid item>
        <DimensionSelect
          options={drillTo}
          onChange={(_, v) => onChangeNextLevel(v)}
          value={nextLevel}
          label={'Next level'}
        />
      </FixedGrid>
    </Grid>
  );
};

function getLevelLabel(breakdownIndex) {
  const dimensionIndex = breakdownIndex + 1;
  return `${dimensionIndex + 1}${orderSuffix(dimensionIndex)} Dimension`;
}

function orderSuffix(index) {
  switch (index) {
    case 0:
      return 'st';
    case 1:
      return 'nd';
    case 2:
      return 'rd';
    default:
      return 'th';
  }
}
